import React, { useCallback, useEffect, useState } from 'react';

import { Card } from '@buildoutinc/blueprint-react/ui/Card';
import { Empty } from '@buildoutinc/blueprint-react/ui/Empty';
import { Table } from '@buildoutinc/blueprint-react/ui/Table';

import { Alert } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import LoadingSpinner from 'components/ui/loading_spinner';
import { showSnackbar } from 'components/ui/snackbar/helper';
import { deleteRequest, getRaw, post } from 'helpers/api';
import { pluralize } from 'helpers/formatters/string_formats';

import OrientationBadge from '../orientation_badge';
import { Orientation } from '../types';
import DocumentPicker, { PickerMode } from '../page/document_picker';
import type { SlotDocument, SlotDocumentsPayload } from './types';

interface Props {
  companyId: string | null;
  slotDocumentsUrl: string;
}

// The steps BSD-38883 runs when a document is created, in order.
const MATCHING_STEPS = [
  {
    title: "Pages tagged with the property's market",
    detail: 'Market is the Census metro or micro area (CBSA) the property sits in, from the OMB July 2023 ' +
      'delineations. Properties outside every CBSA use the closest one.',
  },
  {
    title: 'Pages tagged with the whole metro',
    detail: "For a property in one of a large metro's divisions, when no page is tagged with the division.",
  },
  {
    title: 'No page',
    detail: 'The document gets an empty Market Overview placeholder the broker can fill.',
  },
];

const positionLabel = (document: SlotDocument) => {
  if (document.last_page) return 'Last page';
  if (document.position) return `Page ${document.position}`;

  return '---';
};

const addedMessage = (count: number) => `Added the slot to ${pluralize(count, 'document')}.`;

export default function SlotSettings({ companyId, slotDocumentsUrl }: Props) {
  const [payload, setPayload] = useState<SlotDocumentsPayload | null>(null);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const companyQuery = companyId ? `?company_id=${companyId}` : '';
  const companyParams = { companyId: companyId || undefined };

  const fetchDocuments = useCallback(async () => {
    try {
      setPayload(await getRaw(`${slotDocumentsUrl}${companyQuery}`));
      setHasLoadError(false);
    } catch {
      setHasLoadError(true);
    }
  }, [slotDocumentsUrl, companyQuery]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const addSlot = async (documentIds: number[]) => {
    setIsSaving(true);
    try {
      await post(slotDocumentsUrl, { ...companyParams, documentTemplateIds: documentIds });
      showSnackbar({ text: addedMessage(documentIds.length), variant: 'success' });
      setIsPickerOpen(false);
      await fetchDocuments();
    } catch {
      showSnackbar({ text: "The slot couldn't be added. Try again.", variant: 'danger' });
    } finally {
      setIsSaving(false);
    }
  };

  const removeSlot = async (document: SlotDocument) => {
    setIsSaving(true);
    try {
      await deleteRequest(slotDocumentsUrl, {
        params: { ...companyParams, documentTemplateIds: [document.document_template_id] },
      });
      showSnackbar({ text: `Removed the slot from ${document.document_template_name}.`, variant: 'success' });
      await fetchDocuments();
    } catch {
      showSnackbar({ text: "The slot couldn't be removed. Try again.", variant: 'danger' });
    } finally {
      setIsSaving(false);
    }
  };

  const renderDocuments = () => {
    if (hasLoadError) {
      return (
        <Alert severity="destructive">
          <Alert.Title>The documents with the slot could not be loaded.</Alert.Title>
          <Button variant="secondary" onClick={fetchDocuments}>Try again</Button>
        </Alert>
      );
    }

    if (!payload) return <LoadingSpinner size="md" skipTopBuffer />;

    if (payload.documents.length === 0) {
      return (
        <Empty>
          <Empty.Title>No documents have the slot yet</Empty.Title>
          <Empty.Content>Add the slot to a document to fill it with market pages when it&apos;s created.</Empty.Content>
        </Empty>
      );
    }

    // A parent company's documents span its companies, but the endpoints only take this library's own templates,
    // so a document that isn't one of its candidates is listed without a Remove it couldn't carry out.
    const isRemovable = (documentId: number) => (
      payload.candidates.some(doc => doc.in_document && doc.id === documentId)
    );

    return (
      <Table className="align-middle">
        <Table.Header>
          <Table.Row>
            <Table.Head>Document</Table.Head>
            <Table.Head>Orientation</Table.Head>
            <Table.Head>Position</Table.Head>
            <Table.Head />
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {payload.documents.map(document => (
            <Table.Row key={document.document_template_id}>
              <Table.Cell>{document.document_template_name}</Table.Cell>
              <Table.Cell>
                <OrientationBadge orientation={(document.orientation ?? Orientation.Unknown) as Orientation} />
              </Table.Cell>
              <Table.Cell className="text-muted">{positionLabel(document)}</Table.Cell>
              <Table.Cell className="text-end">
                <Button
                  disabled={isSaving || !isRemovable(document.document_template_id)}
                  size="sm"
                  variant="ghost"
                  onClick={() => removeSlot(document)}
                >
                  Remove
                </Button>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    );
  };

  return (
    <>
      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-1">
            <h5 className="mb-0">Where the slot appears</h5>
            <Button disabled={!payload} size="sm" variant="primary" onClick={() => setIsPickerOpen(true)}>
              Add slot to documents
            </Button>
          </div>
          <p className="text-muted mb-3">
            Add the slot to a document once. It fills with the matching market pages every time that document is
            created.
          </p>
          {renderDocuments()}
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h5 className="mb-1">Matching order</h5>
          <p className="text-muted mb-3">
            How the slot picks pages for a property. The first step that finds any wins.
          </p>
          <ol className="mb-3">
            {MATCHING_STEPS.map(step => (
              <li className="mb-2" key={step.title}>
                <strong>{step.title}</strong>
                <div className="text-muted">{step.detail}</div>
              </li>
            ))}
          </ol>
          <p className="text-muted mb-0">
            When several pages match at the same step, all of them go into the document at the slot&apos;s position.
          </p>
        </Card.Body>
      </Card>

      {payload && (
        <DocumentPicker
          documents={payload.candidates}
          key={isPickerOpen ? 'open' : 'closed'}
          mode={PickerMode.Add}
          pageNames={['Market Overview slot']}
          pageOrientation={Orientation.Unknown}
          saving={isSaving}
          show={isPickerOpen}
          submitLabel="Add slot"
          onClose={() => setIsPickerOpen(false)}
          onSubmit={addSlot}
        />
      )}
    </>
  );
}
